<template>
  <main>
    <b-overlay :show="loading">
      <validation-observer ref="form">
        <b-alert show variant="primary" class="mb-2">
          <strong>Cara penempatan:</strong>
          (1) Pilih no. invoice penerimaan →
          (2) Klik baris barang di kiri →
          (3) Isi gudang, lokasi, dan jumlah →
          (4) Klik <em>Tambah ke antrian</em> (bisa berulang untuk barang lain) →
          (5) Klik <em>Simpan penempatan</em>.
        </b-alert>

        <b-alert
          v-if="hasSharedBarang"
          show
          variant="warning"
          class="mb-2"
        >
          Beberapa baris di invoice ini memakai <strong>barang yang sama</strong>.
          Sisa dihitung per barang (bukan per baris detail), jadi penempatan
          satu baris akan mengurangi sisa semua baris dengan barang tersebut.
        </b-alert>

        <b-card class="mb-2">
          <b-row>
            <b-col sm="12" md="6">
              <b-form-group v-if="!isInvoiceAvailable" label="Pilih No. Invoice">
                <v-select
                  v-model="selectedInvoice"
                  :reduce="(option) => option.value"
                  :options="invoiceList"
                  label="text"
                  placeholder="Cari / pilih invoice..."
                />
              </b-form-group>
              <div v-else>
                <small class="text-muted d-block">Penerimaan</small>
                <strong>ID {{ pb.id }} — No. Invoice: {{ pb.no_invoice }}</strong>
              </div>
            </b-col>
            <b-col sm="12" md="6" class="d-flex align-items-end justify-content-md-end">
              <b-button
                v-b-tooltip.hover.right="'Tambah lokasi penyimpanan kosong'"
                class="mt-2"
                variant="outline-primary"
                @click.prevent="$router.push(`/penyimpanan/add`)"
              >
                <span class="align-middle">
                  <feather-icon icon="PlusIcon" /> Penyimpanan Barang
                </span>
              </b-button>
            </b-col>
          </b-row>
        </b-card>

        <b-row>
          <!-- List barang di penerimaan -->
          <b-col sm="12" md="6">
            <b-card>
              <h5 class="mb-1">Barang dari penerimaan</h5>
              <p class="text-muted small mb-2">
                Klik baris untuk memilih barang yang akan ditempatkan.
              </p>

              <div
                v-if="!form.penerimaan_id"
                class="text-center text-muted py-4"
              >
                Pilih no. invoice terlebih dahulu.
              </div>
              <div
                v-else-if="!barangs.length"
                class="text-center text-muted py-4"
              >
                Tidak ada barang pada penerimaan ini.
              </div>
              <div v-else class="barang-pick-list">
                <div
                  v-for="item in barangs"
                  :key="item.id"
                  class="barang-pick-item"
                  :class="{ 'is-selected': isBarangSelected(item) }"
                  role="button"
                  tabindex="0"
                  @click="selectBarang(item)"
                  @keydown.enter.prevent="selectBarang(item)"
                >
                  <div class="d-flex align-items-start justify-content-between">
                    <div class="pr-1">
                      <div class="barang-pick-name">
                        {{ item.barang ? item.barang.nama : "-" }}
                        <b-badge
                          v-if="isSharedBarang(item)"
                          variant="warning"
                          class="ml-50"
                        >
                          sama
                        </b-badge>
                      </div>
                      <div
                        class="barang-pick-sisa"
                        :class="{ 'is-empty': effectiveSisa(item) <= 0 }"
                      >
                        Sisa: {{ effectiveSisa(item) }}
                        {{
                          item.barang && item.barang.satuan
                            ? item.barang.satuan.satuan
                            : ""
                        }}
                      </div>
                    </div>
                    <feather-icon
                      v-if="isBarangSelected(item)"
                      icon="CheckCircleIcon"
                      size="22"
                      class="barang-pick-check flex-shrink-0"
                    />
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>

          <!-- Form penempatan -->
          <b-col sm="12" md="6">
            <b-card>
              <h5 class="mb-1">Form penempatan</h5>
              <p class="text-muted small mb-2">
                <template v-if="selectedBarang && selectedBarang.barang">
                  Barang dipilih:
                  <strong>{{ selectedBarang.barang.nama }}</strong>
                  — sisa efektif
                  <strong>{{ effectiveSisa(selectedBarang) }}</strong>
                  {{
                    selectedBarang.barang.satuan
                      ? selectedBarang.barang.satuan.satuan
                      : ""
                  }}
                </template>
                <template v-else>
                  Pilih barang di tabel kiri sebelum mengisi lokasi &amp; jumlah.
                </template>
              </p>

              <fieldset :disabled="!selectedBarang">
                <b-form-group v-if="!isInvoiceAvailable" hidden>
                  <validation-provider
                    #default="{ errors }"
                    name="penerimaan"
                    rules="required"
                  >
                    <label for="">Pilih Penerimaan</label>
                    <v-select
                      :options="invoiceList"
                      v-model="form.penerimaan_id"
                      :required="!form.penerimaan_id"
                      :reduce="(option) => option.value"
                      label="text"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="gudang"
                    rules="required"
                  >
                    <label for="">Pilih Gudang <span class="text-danger">*</span></label>
                    <v-select
                      :options="gudangs"
                      v-model="form.gudang_id"
                      :required="!form.gudang_id"
                      :reduce="(option) => option.value"
                      label="text"
                      placeholder="Pilih gudang"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group>
                  <validation-provider
                    #default="{ errors }"
                    name="blok"
                    rules="required"
                  >
                    <label for="">Pilih Blok <span class="text-danger">*</span></label>
                    <v-select
                      :options="bloks"
                      v-model="form.blok_id"
                      :required="!form.blok_id"
                      :reduce="(option) => option.value"
                      label="text"
                      placeholder="Pilih blok"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group>
                  <label for="">Pilih Pallet <span class="text-muted">(opsional)</span></label>
                  <v-select
                    :options="palets"
                    v-model="form.palet_id"
                    :reduce="(option) => option.value"
                    label="text"
                    placeholder="Opsional"
                  />
                </b-form-group>

                <b-form-group>
                  <label for="">Pilih Rak <span class="text-muted">(opsional)</span></label>
                  <v-select
                    v-model="form.rak_id"
                    :options="raks"
                    :reduce="(option) => option.value"
                    label="text"
                    placeholder="Opsional"
                    class="mb-2"
                  />
                </b-form-group>

                <b-form-group>
                  <label for="">Pilih Laci <span class="text-muted">(opsional)</span></label>
                  <v-select
                    v-model="form.laci_id"
                    :options="lacis"
                    :reduce="(option) => option.value"
                    label="text"
                    placeholder="Opsional"
                    class="mb-2"
                  />
                </b-form-group>

                <b-row>
                  <b-col md="6">
                    <b-form-group
                      label="Jumlah input"
                      label-for="nilai"
                      description="Qty sebelum dikalikan konversi satuan"
                    >
                      <b-form-input
                        id="nilai"
                        v-model="nilai"
                        type="number"
                        min="0"
                        placeholder="0"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Konversi satuan"
                      label-for="konversi"
                      description="Default pcs / satuan dasar"
                    >
                      <b-form-select
                        id="konversi"
                        v-model="id_satuan"
                        :options="optkonversi"
                        placeholder="konversi"
                      >
                        <template #first>
                          <b-form-select-option :value="1">
                            pcs (×1)
                          </b-form-select-option>
                        </template>
                      </b-form-select>
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col md="6">
                    <b-form-group
                      label="Jumlah hasil"
                      label-for="jumlah"
                      description="Harus ≤ sisa efektif barang dipilih"
                    >
                      <b-form-input
                        id="jumlah"
                        v-model="form.jumlah"
                        type="number"
                        min="1"
                        placeholder="Jumlah"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <b-form-group class="mb-0">
                  <b-button variant="primary" @click.prevent="addBarang">
                    Tambah ke antrian
                  </b-button>
                </b-form-group>
              </fieldset>
            </b-card>
          </b-col>

          <!-- Antrian penempatan -->
          <b-col cols="12" class="mt-2">
            <b-card>
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <h5 class="mb-0">Antrian penempatan</h5>
                  <small class="text-muted">
                    {{ addedItems.length }} item siap disimpan
                  </small>
                </div>
                <b-button
                  variant="primary"
                  :disabled="!addedItems.length"
                  @click.prevent="submit"
                >
                  Simpan penempatan
                </b-button>
              </div>

              <div
                v-if="!addedItems.length"
                class="text-center text-muted py-4 border rounded"
              >
                Belum ada barang di antrian. Isi form lalu klik
                <strong>Tambah ke antrian</strong>.
              </div>
              <b-table
                v-else
                :fields="fieldsAddedItems"
                bordered
                striped
                responsive
                :items="addedItems"
              >
                <template #cell(no)="row">
                  {{ row.index + 1 }}
                </template>
                <template #cell(lokasi)="{ item }">
                  <strong>{{ item.lokasi }}</strong>
                </template>
                <template #cell(action)="row">
                  <b-link
                    class="text-danger"
                    title="Hapus dari antrian"
                    @click.prevent="removeQueuedItem(row.index)"
                  >
                    <feather-icon size="24" icon="XCircleIcon" />
                  </b-link>
                </template>
              </b-table>
            </b-card>
          </b-col>
        </b-row>
      </validation-observer>
    </b-overlay>
  </main>
</template>

<script>
import {
  BTable,
  BOverlay,
  BFormSelect,
  BFormSelectOption,
  BLink,
  BFormGroup,
  BRow,
  BCol,
  BCard,
  BButton,
  BFormInput,
  BAlert,
  BBadge,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { extend } from "vee-validate";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "vee-validate/dist/rules";

extend("required", {
  ...required,
  message: "This field is required",
});
extend("positive", (value) => {
  return value > 0;
});

export default {
  components: {
    vSelect,
    BFormSelect,
    BFormSelectOption,
    BLink,
    ValidationObserver,
    ValidationProvider,
    BOverlay,
    BFormGroup,
    BTable,
    BRow,
    BCol,
    BCard,
    BButton,
    BFormInput,
    BAlert,
    BBadge,
  },
  watch: {
    "selectedBarang.barang.id"(barang_id) {
      this.resetKonversiState();
      if (barang_id) {
        this.getKonversi(barang_id);
      }
    },
    nilai(value2) {
      const hasil = Number(value2) * Number(this.id_satuan);
      this.form.jumlah = hasil;
    },
    id_satuan(value) {
      const hasil = Number(value) * Number(this.nilai);
      this.form.jumlah = hasil;
    },
    "form.gudang_id"(gudang_id) {
      this.getBlok(gudang_id);
    },
    "form.blok_id"(blok_id) {
      this.getpalet(blok_id);
    },
    "form.palet_id"(palet_id) {
      this.getRak(palet_id);
    },
    "form.rak_id"(rak_id) {
      this.getLaci(rak_id);
    },
    selectedInvoice(penerimaan_id) {
      const penerimaan =
        this.$store.getters["penerimaan-barang/getData"](penerimaan_id);
      if (!penerimaan) {
        this.displayError({
          message: "Data Penerimaan tidak ditemukan, harap pilih yang lain",
        });
        return false;
      }

      this.pb = penerimaan;
      this.form.penerimaan_id = this.pb.id;
      this.params = {
        penerimaan_id,
      };
      this.selectedBarang = null;
      this.addedItems = [];
      this.resetKonversiState();
      this.getBarangPenerimaan();
    },
  },
  data: () => ({
    params: {},
    loading: false,
    selectedInvoice: null,
    selectedBarang: null,
    invoiceLockedFromQuery: false,
    addedItems: [],
    fieldsAddedItems: [
      { key: "no", label: "No" },
      { key: "barang", label: "Barang" },
      { key: "jumlah", label: "Jumlah" },
      { key: "lokasi", label: "Lokasi" },
      { key: "action", label: "#" },
    ],
    bloks: [],
    pb: {
      id: null,
      no_invoice: null,
    },
    raks: [],
    lacis: [],
    palets: [],
    gudangs: [],
    barangs: [],
    form: {
      penerimaan_id: null,
      gudang_id: null,
      blok_id: null,
      palet_id: null,
      rak_id: null,
      laci_id: null,
      jumlah: null,
    },
    invoiceList: [],
    optkonversi: [],
    id_satuan: 1,
    hasil: 0,
    nilai: 0,
  }),
  computed: {
    isInvoiceAvailable() {
      return !!(this.$route.query.inv || this.invoiceLockedFromQuery);
    },
    sharedBarangIdSet() {
      const counts = {};
      this.barangs.forEach((row) => {
        const id = row.barang && row.barang.id;
        if (id == null) return;
        counts[id] = (counts[id] || 0) + 1;
      });
      const shared = {};
      Object.keys(counts).forEach((id) => {
        if (counts[id] > 1) shared[id] = true;
      });
      return shared;
    },
    hasSharedBarang() {
      return Object.keys(this.sharedBarangIdSet).length > 0;
    },
  },
  methods: {
    toastWarning(message) {
      this.$bvToast.toast(message, {
        title: "Peringatan",
        variant: "danger",
        solid: true,
        toaster: "b-toaster-bottom-right",
      });
    },
    resetKonversiState() {
      this.nilai = 0;
      this.id_satuan = 1;
      this.form.jumlah = null;
      this.optkonversi = [];
    },
    selectBarang(item) {
      if (!item) return;
      // Click again to unselect
      if (this.isBarangSelected(item)) {
        this.selectedBarang = null;
        this.resetKonversiState();
        return;
      }
      this.selectedBarang = item;
    },
    isBarangSelected(item) {
      if (!this.selectedBarang || !item) return false;
      return this.selectedBarang.id === item.id;
    },
    isSharedBarang(item) {
      const id = item.barang && item.barang.id;
      return id != null && !!this.sharedBarangIdSet[id];
    },
    applyDefaultQtyFromSisa() {
      if (!this.selectedBarang) return;
      const sisa = this.effectiveSisa(this.selectedBarang);
      this.nilai = sisa > 0 ? sisa : 0;
      this.form.jumlah = Number(this.nilai) * Number(this.id_satuan || 1);
    },
    queuedJumlahFor(item) {
      const barangId = item && item.barang ? item.barang.id : null;
      if (barangId == null) return 0;
      return this.addedItems
        .filter((row) => row.barang_id == barangId)
        .reduce((sum, row) => sum + Number(row.jumlah || 0), 0);
    },
    effectiveSisa(item) {
      if (!item) return 0;
      return Number(item.sisa || 0) - this.queuedJumlahFor(item);
    },
    removeQueuedItem(index) {
      this.addedItems.splice(index, 1);
    },
    buildPayload() {
      // Whitelist fields expected by POST /input/gudang-penyimpanan
      return this.addedItems.map((item) => ({
        penerimaan_id: item.penerimaan_id,
        gudang_id: item.gudang_id,
        blok_id: item.blok_id,
        palet_id: item.palet_id || null,
        rak_id: item.rak_id || null,
        laci_id: item.laci_id || null,
        barang_id: item.barang_id,
        jumlah: Number(item.jumlah),
      }));
    },
    async getKonversi(barang_id) {
      const optkonversi = await this.$store.dispatch("konversi/getData", {
        id_barang: barang_id,
      });
      this.optkonversi = [];
      (optkonversi || []).forEach((konv) => {
        this.optkonversi.push({
          value: konv.nilai,
          text: konv.satuan && konv.satuan.satuan ? konv.satuan.satuan : "-",
        });
      });
      // Default pcs / satuan dasar (×1) — sisa sudah dalam satuan terkecil
      this.id_satuan = 1;
      this.applyDefaultQtyFromSisa();
    },
    submit() {
      if (!this.addedItems.length) {
        this.toastWarning("Antrian penempatan masih kosong");
        return false;
      }

      const payload = this.buildPayload();

      this.loading = true;
      this.$store
        .dispatch("penyimpanan/save", payload)
        .then(() => {
          this.loading = false;
          this.displaySuccess({
            message: "Stok Barang berhasil ditambahkan!",
          });

          setTimeout(() => {
            this.$router.push("/stock-in");
          }, 1000);
        })
        .catch((e) => {
          this.loading = false;
          this.displayError(e);
          return false;
        });
    },
    addBarang() {
      if (!this.selectedBarang || !this.selectedBarang.barang) {
        this.toastWarning("Pilih barang terlebih dahulu");
        return false;
      }

      const jumlah = Number(this.form.jumlah);
      if (!jumlah || Number.isNaN(jumlah) || jumlah < 1) {
        this.toastWarning("Jumlah minimal 1");
        return false;
      }

      const sisaEfektif = this.effectiveSisa(this.selectedBarang);
      if (jumlah > sisaEfektif) {
        this.toastWarning(
          "Jumlah yang diisi tidak boleh lebih dari Sisa jumlah barang"
        );
        return false;
      }

      this.$refs.form.validate().then((success) => {
        if (success) {
          const data = {
            penerimaan_id: this.form.penerimaan_id,
            gudang_id: this.form.gudang_id,
            blok_id: this.form.blok_id,
            palet_id: this.form.palet_id || null,
            rak_id: this.form.rak_id || null,
            laci_id: this.form.laci_id || null,
            jumlah,
            barang:
              this.selectedBarang && this.selectedBarang.barang
                ? this.selectedBarang.barang.nama
                : null,
            barang_id:
              this.selectedBarang && this.selectedBarang.barang
                ? this.selectedBarang.barang.id
                : null,
          };

          const blok = this.bloks.find((b) => b.value == data.blok_id);
          const palet = data.palet_id
            ? this.palets.find((p) => p.value == data.palet_id)
            : null;
          const rak = data.rak_id
            ? this.raks.find((r) => r.value == data.rak_id)
            : null;
          const laci = data.laci_id
            ? this.lacis.find((l) => l.value == data.laci_id)
            : null;

          let lokasi = blok ? `Blok: ${blok.text}` : "";
          if (palet) lokasi += `, Palet: ${palet.text}`;
          if (rak) lokasi += `, Rak: ${rak.text}`;
          if (laci) lokasi += `, Laci: ${laci.text}`;
          data.lokasi = lokasi;

          this.addedItems.push(data);
          this.form.blok_id = null;
          this.form.palet_id = null;
          this.form.rak_id = null;
          this.form.laci_id = null;
          this.nilai = 0;
          this.id_satuan = 1;
          this.form.jumlah = null;
        }
      });
    },
    getGudang() {
      this.gudangs = [];
      this.$store.dispatch("gudang/getData", { jenis: 1 }).then((gudangs) => {
        gudangs.forEach((gudang) => {
          if (this.isAdminGudang) {
            const itsMyGudang = gudang.id == this.myGudang.id;
            if (itsMyGudang) {
              this.gudangs.push({
                value: gudang.id,
                text: gudang.nama_gudang,
              });
            }
          } else {
            this.gudangs.push({
              value: gudang.id,
              text: gudang.nama_gudang,
            });
          }
        });
      });
    },
    getBlok(gudang_id) {
      this.bloks = [];
      this.$store.dispatch("blok/getData", { gudang_id }).then((bloks) => {
        bloks.forEach((blok) => {
          this.bloks.push({
            value: blok.id,
            text: blok.blok,
          });
        });
      });
    },
    getpalet(blok_id) {
      this.palets = [];
      this.$store.dispatch("pallet/getData", { blok_id }).then((palets) => {
        palets.forEach((palet) => {
          this.palets.push({
            value: palet.id,
            text: palet.palet,
          });
        });
      });
    },
    getRak(palet_id) {
      this.raks = [];
      this.$store.dispatch("rak/getData", { palet_id }).then((raks) => {
        raks.forEach((rak) => {
          this.raks.push({
            value: rak.id,
            text: rak.rak,
          });
        });
      });
    },
    getLaci(rak_id) {
      this.lacis = [];
      this.$store.dispatch("laci/getData", { rak_id }).then((lacis) => {
        lacis.forEach((laci) => {
          this.lacis.push({
            value: laci.id,
            text: laci.laci,
          });
        });
      });
    },
    applyInvoiceFromQuery(penerimaanBarangs) {
      const list = Array.isArray(penerimaanBarangs) ? penerimaanBarangs : [];

      // Legacy: ?inv=<id>
      const invId = this.$route.query.inv;
      if (invId != null && invId !== "") {
        const byId = list.find((p) => String(p.id) === String(invId));
        if (byId) {
          this.invoiceLockedFromQuery = true;
          this.selectedInvoice = byId.id;
          return;
        }
        this.displayError({
          message: "Penerimaan dari query inv tidak ditemukan di daftar",
        });
        return;
      }

      // ?no_inv=<no_invoice> e.g. /penempatan?no_inv=INV/001/12839922
      const noInvRaw = this.$route.query.no_inv;
      if (noInvRaw == null || noInvRaw === "") return;

      let needle = String(noInvRaw).trim();
      try {
        needle = decodeURIComponent(needle).trim();
      } catch (e) {
        // already decoded by router
      }

      const found = list.find((p) => {
        const no = String(p.no_invoice || "").trim();
        return no === needle || no.toLowerCase() === needle.toLowerCase();
      });

      if (found) {
        this.invoiceLockedFromQuery = true;
        this.selectedInvoice = found.id;
      } else {
        this.displayError({
          message: `Invoice "${needle}" tidak ditemukan di daftar penerimaan`,
        });
      }
    },
    getBarangPenerimaan() {
      this.$store
        .dispatch("penerimaan-barang/getDataBarang", this.params)
        .then((barangs) => {
          this.barangs = barangs;
        })
        .catch((e) => this.displayError(e));
    },
    getInvoicePenerimaan() {
      const params = { where: { selesai: 0 } };
      if (this.isAdminGudang) {
        params.gudang_id = this.myGudang.id;
        this.form.gudang_id = this.myGudang.id;
      }
      this.loading = true;
      this.$store
        .dispatch("penerimaan-barang/getData", params)
        .then((penerimaanBarangs) => {
          this.loading = false;
          this.invoiceList = [];
          (penerimaanBarangs || []).forEach((penerimaan) => {
            this.invoiceList.push({
              value: penerimaan.id,
              text: penerimaan.no_invoice,
            });
          });

          this.applyInvoiceFromQuery(penerimaanBarangs);

          this.getGudang();
        })
        .catch((e) => {
          this.loading = false;
          this.displayError(e);
          return false;
        });
    },
  },
  created() {
    this.getInvoicePenerimaan();
  },
};
</script>

<style lang="scss" scoped>
.barang-pick-list {
  max-height: 480px;
  overflow-y: auto;
}

.barang-pick-item {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.5rem;
  padding: 0.75rem 0.9rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background-color 0.15s ease;
  background: transparent;

  &:hover {
    border-color: rgba(40, 199, 111, 0.55);
    box-shadow: 0 0 0 1px rgba(40, 199, 111, 0.2);
  }

  &.is-selected {
    border-color: #28c76f;
    box-shadow: 0 0 0 2px rgba(40, 199, 111, 0.35),
      0 0 14px rgba(40, 199, 111, 0.25);
    background: rgba(40, 199, 111, 0.08);
  }
}

.barang-pick-name {
  font-weight: 600;
  line-height: 1.3;
  margin-bottom: 0.25rem;
}

.barang-pick-sisa {
  color: #059669;
  font-weight: 700;
  text-shadow: 0 0 10px rgba(16, 185, 129, 0.55);

  &.is-empty {
    color: #ea5455;
    text-shadow: 0 0 8px rgba(234, 84, 85, 0.35);
  }
}

.barang-pick-check {
  color: #28c76f;
  filter: drop-shadow(0 0 4px rgba(40, 199, 111, 0.65));
}

.dark-layout {
  .barang-pick-item {
    border-color: rgba(255, 255, 255, 0.18);

    &:hover {
      border-color: rgba(52, 211, 153, 0.65);
      box-shadow: 0 0 0 1px rgba(52, 211, 153, 0.25);
    }

    &.is-selected {
      border-color: #34d399;
      box-shadow: 0 0 0 2px rgba(52, 211, 153, 0.4),
        0 0 16px rgba(52, 211, 153, 0.35);
      background: rgba(52, 211, 153, 0.12);
    }
  }

  .barang-pick-sisa {
    color: #34d399;
    text-shadow: 0 0 12px rgba(52, 211, 153, 0.75);

    &.is-empty {
      color: #f87272;
      text-shadow: 0 0 10px rgba(248, 114, 114, 0.55);
    }
  }

  .barang-pick-check {
    color: #34d399;
    filter: drop-shadow(0 0 6px rgba(52, 211, 153, 0.8));
  }
}
</style>
